/**
 * ReportMaterialProblemDialog — Phase 4 installer-side capture UX.
 *
 * Single-purpose modal: capture a structured FieldMaterialFeedbackEvent
 * via the canonical `recordFieldMaterialFeedback` writer. No side effects
 * beyond the append-only write. No notifications, no automation.
 */
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useProcurementWriters } from "@/data/procurement/procurementWriters";
import type {
  FieldMaterialIssueType,
  FieldMaterialSeverity,
} from "@/data/VendorPOContext";
import { toast } from "@/hooks/use-toast";

const ISSUE_TYPES: { value: FieldMaterialIssueType; label: string }[] = [
  { value: "missing_material", label: "Missing material" },
  { value: "wrong_material", label: "Wrong material" },
  { value: "damaged_material", label: "Damaged material" },
  { value: "incomplete_kit", label: "Incomplete kit" },
  { value: "quantity_shortfall", label: "Quantity shortfall" },
  { value: "spec_incompatibility", label: "Spec incompatibility" },
  { value: "substitution_problem", label: "Substitution problem" },
  { value: "other", label: "Other" },
];

const SEVERITIES: { value: FieldMaterialSeverity; label: string; hint: string }[] = [
  { value: "blocker", label: "Blocker", hint: "Cannot install without this fixed" },
  { value: "warn", label: "Warning", hint: "Install may continue but at risk" },
  { value: "info", label: "Info", hint: "Heads-up only" },
];

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  jobId: string;
  actor: string;
  phase?: string;
}

export function ReportMaterialProblemDialog({
  open, onOpenChange, jobId, actor, phase,
}: Props) {
  const { recordFieldMaterialFeedback } = useProcurementWriters();
  const [issueType, setIssueType] = useState<FieldMaterialIssueType>("missing_material");
  const [severity, setSeverity] = useState<FieldMaterialSeverity>("blocker");
  const [description, setDescription] = useState("");
  const [materialReference, setMaterialReference] = useState("");

  function reset() {
    setIssueType("missing_material");
    setSeverity("blocker");
    setDescription("");
    setMaterialReference("");
  }

  function handleSubmit() {
    try {
      recordFieldMaterialFeedback(
        {
          jobId,
          issueType,
          severity,
          description,
          ...(materialReference ? { materialReference } : {}),
          ...(phase ? { phase } : {}),
        },
        actor,
      );
      toast({ title: "Reported", description: "Field material issue recorded." });
      reset();
      onOpenChange(false);
    } catch (err) {
      toast({
        title: "Could not submit",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Report Material Problem</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Issue type</Label>
            <select
              data-testid="field-feedback-issue-type"
              className="w-full mt-1 h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={issueType}
              onChange={(e) => setIssueType(e.target.value as FieldMaterialIssueType)}
            >
              {ISSUE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Severity</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {SEVERITIES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  data-testid={`field-feedback-severity-${s.value}`}
                  onClick={() => setSeverity(s.value)}
                  className={`text-xs rounded-md border px-2 py-1.5 ${
                    severity === s.value
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground"
                  }`}
                  title={s.hint}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Material reference (optional)</Label>
            <Input
              className="mt-1 h-9 text-sm"
              placeholder="SKU, part name, or PO #"
              value={materialReference}
              onChange={(e) => setMaterialReference(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs">Description *</Label>
            <Textarea
              data-testid="field-feedback-description"
              className="mt-1 text-sm"
              rows={3}
              placeholder="What did you find on site?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          {phase && (
            <p className="text-[10px] text-muted-foreground">
              Phase: <span className="font-mono">{phase}</span>
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            data-testid="field-feedback-submit"
            disabled={!description.trim()}
            onClick={handleSubmit}
          >
            Submit report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
